import {
  activationStatusSchema,
  accessExplanationSchema,
  auditEventSchema,
  currentPrincipalSchema,
  groupSchema,
  permissionGrantSchema,
  permissionOperationDefinitionSchema,
  provisioningResultSchema,
  syncEnvelopeSchema,
  userDetailsSchema,
  userPageSchema,
  personAdministrativeNoteSchema,
  type PersonAdministrativeNote,
  type UpdatePersonDescription,
  type CreatePersonAdministrativeNote,
  type UpdatePersonAdministrativeNote,
  type AccessExplanation,
  type AccountTransition,
  type AccountState,
  type ActivationStatus,
  type AssignGroupMembership,
  type AuditEvent,
  type BusinessProfileType,
  type CreateGroup,
  type CreatePermissionGrant,
  type CreateUser,
  type CompleteActivation,
  type CurrentPrincipal,
  type Group,
  type PermissionGrant,
  type PermissionOperationDefinition,
  type GrantBusinessProfile,
  type SyncEnvelope,
  type UpdateGroup,
  type UserDetails,
  type UserPage,
  type ProvisioningResult,
  type WithdrawBusinessProfile,
} from '@sad5/contracts';
import { z } from 'zod';
import { clientUuid } from './client-id.js';
import { portalRequestPath } from './public-request.js';

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const method = init?.method?.toUpperCase() ?? 'GET';
  const headers = new Headers(init?.headers);
  if (init?.body !== undefined) headers.set('content-type', 'application/json');
  if (method !== 'GET') headers.set('x-sad5-csrf', '1');
  const response = await fetch(portalRequestPath(path), {
    ...init,
    cache: 'no-store',
    credentials: 'same-origin',
    headers,
  });
  if (response.status === 401) {
    window.location.assign(portalRequestPath('/auth/login'));
    throw new Error('Sesja wygasła');
  }
  if (!response.ok) {
    const problem = (await response.json().catch(() => undefined)) as
      { message?: unknown } | undefined;
    throw new Error(
      typeof problem?.message === 'string'
        ? problem.message
        : `Żądanie nie powiodło się (${response.status})`,
    );
  }
  if (response.status === 204) return undefined;
  return response.json() as Promise<unknown>;
}

export interface UserFilters {
  search?: string | undefined;
  state?: AccountState | undefined;
  profileType?: BusinessProfileType | undefined;
  groupId?: string | undefined;
  limit?: number;
  offset?: number;
}

export async function fetchUsers(filters: UserFilters = {}): Promise<UserPage> {
  const query = new URLSearchParams();
  if (filters.search) query.set('search', filters.search);
  if (filters.state) query.set('state', filters.state);
  if (filters.profileType) query.set('profileType', filters.profileType);
  if (filters.groupId) query.set('groupId', filters.groupId);
  query.set('limit', String(filters.limit ?? 100));
  query.set('offset', String(filters.offset ?? 0));
  return userPageSchema.parse(await request(`/api/v1/users?${query}`));
}

export async function downloadUsersCsv(
  filters: UserFilters = {},
): Promise<Blob> {
  const query = new URLSearchParams();
  if (filters.search) query.set('search', filters.search);
  if (filters.state) query.set('state', filters.state);
  if (filters.profileType) query.set('profileType', filters.profileType);
  if (filters.groupId) query.set('groupId', filters.groupId);
  const response = await fetch(
    portalRequestPath(`/api/v1/users/export.csv?${query}`),
    {
      cache: 'no-store',
      credentials: 'same-origin',
    },
  );
  if (response.status === 401) {
    window.location.assign(portalRequestPath('/auth/login'));
    throw new Error('Sesja wygasła');
  }
  if (!response.ok)
    throw new Error(`Nie udało się przygotować eksportu (${response.status})`);
  return response.blob();
}

export async function fetchCurrentPrincipal(): Promise<CurrentPrincipal> {
  return currentPrincipalSchema.parse(await request('/api/v1/me'));
}

export async function fetchUser(id: string): Promise<UserDetails> {
  return userDetailsSchema.parse(await request(`/api/v1/users/${id}`));
}

export async function updatePersonDescription(
  personId: string,
  input: UpdatePersonDescription,
): Promise<Pick<UserDetails, 'personId' | 'description' | 'revision'>> {
  return z
    .object({
      personId: z.string().uuid(),
      description: z.string().max(200).nullable(),
      revision: z.number().int().positive(),
    })
    .parse(
      await request(
        `/api/v1/persons/${encodeURIComponent(personId)}/description`,
        { method: 'PATCH', body: JSON.stringify(input) },
      ),
    );
}

export async function fetchPersonAdministrativeNotes(
  personId: string,
): Promise<PersonAdministrativeNote[]> {
  return z
    .array(personAdministrativeNoteSchema)
    .parse(
      await request(
        `/api/v1/persons/${encodeURIComponent(personId)}/administrative-notes`,
      ),
    );
}

export async function createPersonAdministrativeNote(
  personId: string,
  input: CreatePersonAdministrativeNote,
): Promise<PersonAdministrativeNote> {
  return personAdministrativeNoteSchema.parse(
    await request(
      `/api/v1/persons/${encodeURIComponent(personId)}/administrative-notes`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  );
}

export async function updatePersonAdministrativeNote(
  personId: string,
  noteId: string,
  input: UpdatePersonAdministrativeNote,
): Promise<PersonAdministrativeNote> {
  return personAdministrativeNoteSchema.parse(
    await request(
      `/api/v1/persons/${encodeURIComponent(personId)}/administrative-notes/${encodeURIComponent(noteId)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    ),
  );
}

export async function fetchGroups(): Promise<Group[]> {
  return z.array(groupSchema).parse(await request('/api/v1/groups'));
}

export async function fetchGroupPermissions(
  groupId: string,
): Promise<PermissionGrant[]> {
  return z
    .array(permissionGrantSchema)
    .parse(
      await request(
        `/api/v1/groups/${encodeURIComponent(groupId)}/permissions`,
      ),
    );
}

export async function fetchPermissionCatalog(): Promise<
  PermissionOperationDefinition[]
> {
  return z
    .array(permissionOperationDefinitionSchema)
    .parse(await request('/api/v1/permission-catalog'));
}

export async function createPermissionGrant(
  groupId: string,
  input: CreatePermissionGrant,
): Promise<PermissionGrant> {
  return permissionGrantSchema.parse(
    await request(
      `/api/v1/commands/groups/${encodeURIComponent(groupId)}/permissions`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientUuid() },
        body: JSON.stringify(input),
      },
    ),
  );
}

export async function revokePermissionGrant(
  groupId: string,
  grantId: string,
  revision: number,
  reason: string,
): Promise<PermissionGrant> {
  return permissionGrantSchema.parse(
    await request(
      `/api/v1/commands/groups/${encodeURIComponent(groupId)}/permissions/${encodeURIComponent(grantId)}/revoke`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientUuid() },
        body: JSON.stringify({ reason, revision }),
      },
    ),
  );
}

export async function createGroup(input: CreateGroup): Promise<Group> {
  return groupSchema.parse(
    await request('/api/v1/commands/groups', {
      method: 'POST',
      headers: { 'idempotency-key': clientUuid() },
      body: JSON.stringify(input),
    }),
  );
}

export async function updateGroup(
  id: string,
  input: UpdateGroup,
): Promise<Group> {
  return groupSchema.parse(
    await request(`/api/v1/commands/groups/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'idempotency-key': clientUuid() },
      body: JSON.stringify(input),
    }),
  );
}

export async function assignGroupMembership(
  accountId: string,
  input: AssignGroupMembership,
): Promise<UserDetails> {
  return userDetailsSchema.parse(
    await request(
      `/api/v1/commands/users/${encodeURIComponent(accountId)}/groups`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientUuid() },
        body: JSON.stringify(input),
      },
    ),
  );
}

export async function removeGroupMembership(
  accountId: string,
  groupId: string,
  reason: string,
): Promise<UserDetails> {
  return userDetailsSchema.parse(
    await request(
      `/api/v1/commands/users/${encodeURIComponent(accountId)}/groups/${encodeURIComponent(groupId)}/remove`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientUuid() },
        body: JSON.stringify({ reason }),
      },
    ),
  );
}

export async function fetchAudit(): Promise<AuditEvent[]> {
  return z
    .array(auditEventSchema)
    .parse(await request('/api/v1/audit?limit=100'));
}

export async function fetchSync(cursor = '0'): Promise<SyncEnvelope> {
  return syncEnvelopeSchema.parse(
    await request(`/api/v1/sync?cursor=${encodeURIComponent(cursor)}`),
  );
}

export async function createUser(
  input: CreateUser,
): Promise<ProvisioningResult> {
  return provisioningResultSchema.parse(
    await request('/api/v1/commands/users', {
      method: 'POST',
      headers: { 'idempotency-key': clientUuid() },
      body: JSON.stringify(input),
    }),
  );
}

export async function retryProvisioning(
  id: string,
): Promise<ProvisioningResult> {
  return provisioningResultSchema.parse(
    await request(
      `/api/v1/commands/users/${encodeURIComponent(id)}/retry-provisioning`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientUuid() },
      },
    ),
  );
}

export async function fetchActivationStatus(
  token: string,
): Promise<ActivationStatus> {
  return activationStatusSchema.parse(
    await request(
      `/api/v1/activation/status?token=${encodeURIComponent(token)}`,
    ),
  );
}

export async function completeActivation(
  input: CompleteActivation,
): Promise<void> {
  z.object({ activated: z.literal(true) }).parse(
    await request('/api/v1/activation/complete', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export async function logout(): Promise<{ globalLogoutUrl: string }> {
  return z
    .object({ globalLogoutUrl: z.string().url() })
    .parse(await request('/auth/logout', { method: 'POST' }));
}

export async function blockUser(id: string): Promise<UserDetails> {
  return userDetailsSchema.parse(
    await request(`/api/v1/commands/users/${encodeURIComponent(id)}/block`, {
      method: 'POST',
    }),
  );
}

export async function transitionAccount(
  id: string,
  input: AccountTransition,
): Promise<UserDetails> {
  return userDetailsSchema.parse(
    await request(
      `/api/v1/commands/users/${encodeURIComponent(id)}/transitions`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientUuid() },
        body: JSON.stringify(input),
      },
    ),
  );
}

export async function grantProfile(
  id: string,
  input: GrantBusinessProfile,
): Promise<UserDetails> {
  return userDetailsSchema.parse(
    await request(`/api/v1/commands/users/${encodeURIComponent(id)}/profiles`, {
      method: 'POST',
      headers: { 'idempotency-key': clientUuid() },
      body: JSON.stringify(input),
    }),
  );
}

export async function withdrawProfile(
  id: string,
  profileId: string,
  input: WithdrawBusinessProfile,
): Promise<UserDetails> {
  return userDetailsSchema.parse(
    await request(
      `/api/v1/commands/users/${encodeURIComponent(id)}/profiles/${encodeURIComponent(profileId)}/withdraw`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientUuid() },
        body: JSON.stringify(input),
      },
    ),
  );
}

export async function explainAccess(
  accountId: string,
  operation: string,
): Promise<AccessExplanation> {
  const query = new URLSearchParams({ accountId, operation });
  return accessExplanationSchema.parse(
    await request(`/api/v1/access/explain?${query.toString()}`),
  );
}
